import { useEffect } from "react";
import { identify, Identify } from "@amplitude/analytics-browser";
import { useFlags, type IToggle } from "@unleash/proxy-client-react";

const DISABLED_VARIANT_NAME = "disabled";

interface FeatureFlagProperties {
  featureFlags: string[];
  featureFlagVariants: string[];
}

/**
 * Maps the toggles the user currently has resolved into Amplitude user
 * properties: a list of enabled flag names plus, for any flag exposing an
 * active variant, a `flag:variant` entry.
 *
 * Variant inclusion is gated on `feature_enabled`: `getVariant` returns the
 * configured variant object even when the toggle is disabled, so a name-only
 * check would surface variants for flags that are switched off.
 */
export function buildFeatureFlagProperties(flags: IToggle[]): FeatureFlagProperties {
  const enabledFlags = flags.filter((flag) => flag.enabled);

  const featureFlags = enabledFlags.map((flag) => flag.name).sort();

  const featureFlagVariants = enabledFlags
    .filter((flag) => flag.variant?.feature_enabled === true && flag.variant.name !== DISABLED_VARIANT_NAME)
    .map((flag) => `${flag.name}:${flag.variant.name}`)
    .sort();

  return { featureFlags, featureFlagVariants };
}

/**
 * Keeps Amplitude in sync with the Unleash feature flags (and variants) the
 * user currently has available, exposing them as user properties so events can
 * be correlated with the flags that were active when they fired.
 */
export function useIdentifyAmplitudeFeatureFlags() {
  const flags = useFlags();

  useEffect(() => {
    const { featureFlags, featureFlagVariants } = buildFeatureFlagProperties(flags);

    const identity = new Identify();
    identity.set("featureFlags", featureFlags);
    identity.set("featureFlagVariants", featureFlagVariants);

    identify(identity);
  }, [flags]);
}
